import { assocPath, mergeDeepRight, mergeRight } from 'ramda';

import {
    type AuthenticationActions,
    initializeOnboarding,
    RESET_SECONDARY_AUTHENTICATION,
    resetAuthetication,
    SET_AUTHENTICATION_STATUS,
    SET_ERROR_BY_PATH,
    SET_IS_EXPIRED_PASSWORD_RESET_TRANSACTION,
    SET_IS_VALID_PASSWORD_RESET_TRANSACTION,
    SET_ONBOARDING_INVITATION,
    SET_ONBOARDING_TRANSACTION,
    SET_PASSWORD_RESET_TRANSACTION,
    SET_PRIMARY_LOGIN_ERROR,
    SET_QUICK_LOGIN,
    SET_RETURNING_SSO_USER,
    SET_SSO_ERROR,
    SET_SSO_METHODS,
    setIsExpiredOnboardingTransaction,
    setIsValidOnboardingInvitation,
    setPreviousSecondaryProofId,
    setStep
} from '@accounts/store/authentication.actions';
import { PrimaryProofType, ProofType } from '@accounts/types/authentication';
import { type Authentication } from '@accounts/types/store';

export const initialState: Authentication = {
    step: undefined,
    status: undefined,
    [ProofType.Primary]: {},
    [ProofType.Secondary]: {},
    [PrimaryProofType.SSO]: {}
};

const authenticationReducer = (
    stateParam: Authentication,
    action: AuthenticationActions
): Authentication => {
    const state = stateParam || initialState;
    switch (action.type) {
        case resetAuthetication.type:
            return initialState;
        case RESET_SECONDARY_AUTHENTICATION:
            return mergeRight(state, {
                secondary: {}
            });
        case SET_AUTHENTICATION_STATUS:
            return mergeRight(state, { status: action.payload });
        case SET_QUICK_LOGIN:
            return mergeRight(state, { isQuickLogin: action.payload });
        case setStep.type:
            return mergeRight(state, { step: action.payload });
        case SET_PRIMARY_LOGIN_ERROR:
            return mergeDeepRight(state, {
                primary: { error: action.payload }
            });
        case SET_SSO_METHODS:
            return mergeDeepRight(state, {
                sso: {
                    methods: action.payload
                }
            });
        case SET_RETURNING_SSO_USER:
            return mergeDeepRight(state, {
                sso: {
                    isReturningSSOUser: action.payload
                }
            });
        case SET_SSO_ERROR:
            return mergeDeepRight(state, {
                sso: {
                    error: action.payload
                }
            });
        case SET_ERROR_BY_PATH:
            return assocPath([...action.payload.path, 'error'], action.payload.error, state);
        case SET_IS_EXPIRED_PASSWORD_RESET_TRANSACTION:
            return mergeDeepRight(state, {
                setNewPassword: {
                    isExpired: action.payload
                }
            });
        case SET_IS_VALID_PASSWORD_RESET_TRANSACTION:
            return mergeDeepRight(state, {
                setNewPassword: {
                    isValid: action.payload
                }
            });
        case SET_PASSWORD_RESET_TRANSACTION:
            return mergeRight(state, {
                setNewPassword: {
                    ...state.setNewPassword,
                    transaction: action.payload
                }
            });
        case initializeOnboarding.type:
            return mergeDeepRight(initialState, {
                onboarding: {
                    invitationId: action.payload || state.onboarding?.invitationId
                }
            });
        case SET_ONBOARDING_TRANSACTION:
            return mergeRight(state, {
                onboarding: {
                    ...state.onboarding,
                    transaction: action.payload
                }
            });
        case setIsValidOnboardingInvitation.type:
            return mergeDeepRight(state, {
                onboarding: {
                    isValidInvitation: action.payload
                }
            });
        case SET_ONBOARDING_INVITATION:
            return mergeRight(state, {
                onboarding: {
                    ...state.onboarding,
                    invitation: action.payload
                }
            });
        case setIsExpiredOnboardingTransaction.type:
            return mergeDeepRight(state, {
                onboarding: {
                    isExpired: action.payload
                }
            });
        case setPreviousSecondaryProofId.type:
            return mergeDeepRight(state, {
                secondary: {
                    previousProofId: action.payload
                }
            });
        default:
            return state;
    }
};

export default authenticationReducer;
